/**
 * WhatsApp share links (wa.me) — Phase 1 integration.
 * Opens WhatsApp with a pre-filled message; staff sends manually from their device.
 */

export type WhatsAppMessageType =
  | 'queue_update'
  | 'visit_registered'
  | 'your_turn'
  | 'follow_up'
  | 'prescription';

export interface WhatsAppMessageContext {
  patientName: string;
  hospitalName?: string;
  patientId?: string;
  reason?: string;
  doctorName?: string;
}

/** Normalize Indian and common formats to wa.me digits (no +). */
export function normalizePhoneForWhatsApp(
  contact: string | null | undefined
): string | null {
  if (contact == null || String(contact).trim() === '') return null;
  const digits = String(contact).replace(/\D/g, '');
  if (!digits) return null;

  if (digits.length === 10) {
    return `91${digits}`;
  }
  if (digits.length === 12 && digits.startsWith('91')) {
    return digits;
  }
  if (digits.length === 11 && digits.startsWith('0')) {
    return `91${digits.slice(1)}`;
  }
  if (digits.length >= 10 && digits.length <= 15) {
    return digits;
  }

  return null;
}

export function isValidWhatsAppPhone(contact: string | null | undefined): boolean {
  return normalizePhoneForWhatsApp(contact) !== null;
}

export function buildWhatsAppMessage(
  type: WhatsAppMessageType,
  ctx: WhatsAppMessageContext
): string {
  const clinic = ctx.hospitalName?.trim() || 'our clinic';
  const name = ctx.patientName?.trim() || 'there';

  switch (type) {
    case 'visit_registered': {
      const visitFor = ctx.reason?.trim() || 'your consultation';
      return `Hello ${name},

Welcome to ${clinic}. Your visit has been registered for ${visitFor}.

Please wait in the reception area — we will call you when the doctor is ready.

Thank you.`;
    }
    case 'queue_update':
      return `Hello ${name},

Thank you for visiting ${clinic}. You have been added to our queue for today.

We will message you when it is your turn. Thank you for your patience.`;
    case 'your_turn': {
      const doctorLine = ctx.doctorName?.trim()
        ? `Please proceed to meet Dr. ${ctx.doctorName.trim()}.`
        : 'Please proceed to the consultation room.';
      return `Hello ${name},

It is your turn at ${clinic}. ${doctorLine}

Thank you.`;
    }
    case 'follow_up':
      return `Hello ${name},

This is a friendly reminder from ${clinic} about your follow-up visit.

Please let us know if you need to change the date or time. We look forward to seeing you.

Thank you.`;
    case 'prescription':
      return `Hello ${name},

Your prescription from ${clinic} is ready. Please download and save it for your records.

If you need any help, please contact our clinic.

Thank you.`;
    default:
      return `Hello ${name},

Thank you for visiting ${clinic}. Please contact us if you have any questions about your visit.

Thank you.`;
  }
}

export function buildWhatsAppUrl(
  phone: string | null | undefined,
  message: string
): string | null {
  const normalized = normalizePhoneForWhatsApp(phone);
  if (!normalized) return null;
  return `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
}

export function openWhatsAppChat(
  phone: string | null | undefined,
  message: string
): boolean {
  const url = buildWhatsAppUrl(phone, message);
  if (!url) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

export interface PrescriptionWhatsAppSummary {
  patientName: string;
  hospitalName?: string;
  patientIdFormatted?: string;
  prescriptionDate: string;
  diagnosis?: string;
  chiefComplaints?: string;
  medications: Array<{
    name: string;
    dosage?: string;
    frequency?: string;
    duration?: string;
    instructions?: string;
  }>;
  labTests?: Array<{ name?: string; test_name?: string } | string>;
  doctorName?: string;
  notes?: string;
}

/** Pre-filled WhatsApp text with prescription summary for the patient */
export function buildPrescriptionWhatsAppMessage(
  summary: PrescriptionWhatsAppSummary
): string {
  const clinic = summary.hospitalName?.trim() || 'our clinic';
  const name = summary.patientName?.trim() || 'there';
  const lines: string[] = [
    `Hello ${name},`,
    '',
    `Your prescription from ${clinic} (${summary.prescriptionDate}) is ready:`,
  ];

  if (summary.patientIdFormatted) {
    lines.push(`Patient ID: ${summary.patientIdFormatted}`);
  }
  if (summary.diagnosis?.trim()) {
    lines.push(`Diagnosis: ${summary.diagnosis.trim()}`);
  }
  if (summary.chiefComplaints?.trim()) {
    lines.push(`Complaints: ${summary.chiefComplaints.trim()}`);
  }

  if (summary.medications.length > 0) {
    lines.push('', 'Medications:');
    summary.medications.forEach((med, index) => {
      const parts = [
        med.name,
        med.dosage,
        med.frequency,
        med.duration ? `for ${med.duration}` : '',
      ].filter(Boolean);
      lines.push(`${index + 1}. ${parts.join(' — ')}`);
      if (med.instructions?.trim()) {
        lines.push(`   ${med.instructions.trim()}`);
      }
    });
  }

  if (summary.labTests && summary.labTests.length > 0) {
    const testNames = summary.labTests
      .map((t) =>
        typeof t === 'string' ? t : (t.name || t.test_name || '').trim()
      )
      .filter(Boolean);
    if (testNames.length > 0) {
      lines.push('', `Tests advised: ${testNames.join(', ')}`);
    }
  }

  if (summary.doctorName?.trim()) {
    lines.push('', `Doctor: ${summary.doctorName.trim()}`);
  }
  if (summary.notes?.trim()) {
    lines.push(`Notes: ${summary.notes.trim()}`);
  }

  lines.push(
    '',
    'Please take your medicines as advised. Contact us if you have any questions.',
    '',
    'Thank you.'
  );

  return lines.join('\n');
}
